import React, { useMemo, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography, IconButton, Tooltip } from '@mui/material';
import { FolderOpen as FolderOpenIcon, CheckBox, Cancel } from '@mui/icons-material';
import { useDocs } from './hooks/HDocs';
import UserDocsForm from '../dialogs/UserDocsForm';
import { API_BASE } from '../../config/constants';
import { hashPassword } from '../utils/helpers';
import { useH2FA } from './hooks/H2FA';
import TwoFASetupDialog from '../dialogs/TwoFASetupDialog';

type ProfileTabProps = {
    user?: {
        id?: string | number;
        user_id?: string | number;
        name?: string;
        first_name?: string;
        last_name?: string;
        display_name?: string;
        email?: string;
        user?: string;
        phone_number?: string;
        company?: string;
        company_name?: string;
        role?: string;
        role_name?: string;
        country?: string;
        nda?: boolean;
        [key: string]: any;
    } | null;
    setLoggedInUser?: (user: any) => void;
};

const ProfileTab: React.FC<ProfileTabProps> = ({ user: dbUser, setLoggedInUser }) => {
    const { accounts, inProgress } = useMsal();
    const msalUser = accounts[0];
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // 2FA state
    const [has2FA, setHas2FA] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [qrSecret, setQrSecret] = useState('');
    const [qrCode, setQrCode] = useState('');
    const [disable2FALoading, setDisable2FALoading] = useState(false);
    const { setup2FA, confirm2FA, disable2FA, loading: twoFALoading, error: twoFAError } = useH2FA();

    // Docs dialog state and handler (matches GUsersTable)
    const { uploadUserDocumentToAzure } = useDocs();
    const [docsDialogOpen, setDocsDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | number | null>(null);

    const handleViewDocs = (userId: string | number | null | undefined, userName?: string | null) => {
        setSelectedUserId(userId ?? null);
        setDocsDialogOpen(true);
        // we could store userName if needed in future
    };

    const handleCloseDocsDialog = () => {
        setDocsDialogOpen(false);
        setSelectedUserId(null);
    };

    const handleToggleChangePassword = () => {
        setShowChangePassword((prev) => {
            if (prev) {
                setNewPassword('');
                setConfirmPassword('');
                setPasswordError('');
                setPasswordSuccess('');
            }
            return !prev;
        });
    };

    const userId = useMemo(() => dbUser?.id ?? dbUser?.user_id ?? null, [dbUser]);

    // Check current 2FA status - update when user changes
    useEffect(() => {
        if (dbUser) {
            const hasTOTP = !!(dbUser.totp_secret && typeof dbUser.totp_secret === 'string' && dbUser.totp_secret.trim());
            setHas2FA(hasTOTP);
        }
    }, [dbUser]);

    // Debug logs: verify NDA value from prop vs backend
    useEffect(() => {
        console.log('[ProfileTab] prop dbUser snapshot', dbUser);
        try {
            const authTokenStr = localStorage.getItem('AUTH_TOKEN');
            if (authTokenStr) {
                JSON.parse(authTokenStr);
                // authUser available if needed
            }
            // console.log('[ProfileTab] AUTH_TOKEN decoded', authUser);
        } catch (err) {
            console.warn('[ProfileTab] failed to parse AUTH_TOKEN', err);
        }

        const fetchBackendUser = async () => {
            try {
                const email = dbUser?.email || dbUser?.user;
                if (!email) {
                    console.log('[ProfileTab] no email available to fetch backend user');
                    return;
                }
                const res = await fetch(`${API_BASE}/api/users/get-user-by-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
                const payload = await res.json().catch(() => null);
                // console.log('[ProfileTab] backend response', { ok: res.ok, payload });
                const backendUser = Array.isArray(payload?.data) && payload.data.length > 0 ? payload.data[0] : payload.data ?? payload.user ?? payload;
                console.log('[ProfileTab] backendUser snapshot', backendUser);
                console.log('[ProfileTab] NDA comparison', {
                    propNda: dbUser?.nda,
                    backendNda: backendUser?.nda,
                });
            } catch (err) {
                console.error('[ProfileTab] error fetching backend user for NDA check', err);
            }
        };

        void fetchBackendUser();
    }, [dbUser]);

    const handleLogout = () => {
        localStorage.removeItem('AUTH_TOKEN');
        setLoggedInUser?.(null);
    };

    const displayName =
        dbUser?.name ||
        dbUser?.display_name ||
        [dbUser?.first_name, dbUser?.last_name].filter(Boolean).join(' ').trim() ||
        msalUser?.name ||
        '-';

    const displayEmail = dbUser?.email || dbUser?.user || msalUser?.username || '-';
    const displayCompany = dbUser?.company_name || dbUser?.company || '-';
    const displayRole = dbUser?.role_name || dbUser?.role || '-';
    // Normalize NDA value: backend may return boolean, number (1), or string ('true'/'1')
    const isNdaTrue = (() => {
        const v: any = dbUser?.nda;
        if (v === true) return true;
        if (v === 1) return true;
        if (v === '1') return true;
        if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
        return false;
    })();

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess('');

        if (!userId) {
            setPasswordError('Your user account could not be identified. Please log in again.');
            return;
        }

        if (!newPassword.trim()) {
            setPasswordError('New password is required.');
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters long.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('Password confirmation does not match.');
            return;
        }

        setPasswordLoading(true);
        try {
            const encryptedPassword = hashPassword(newPassword);
            const response = await fetch(`${API_BASE}/api/users/update-user-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    password: encryptedPassword,
                }),
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Failed to update password.');
            }

            setNewPassword('');
            setConfirmPassword('');
            setPasswordSuccess('Password updated successfully.');
        } catch (error: any) {
            setPasswordError(error?.message || 'Failed to update password.');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleEnableTwoFA = async () => {
        if (!userId) {
            alert('User ID could not be identified.');
            return;
        }

        const displayEmail = dbUser?.email || dbUser?.user || msalUser?.username || '';
        const result = await setup2FA(Number(userId), displayEmail);
        if (result) {
            setQrSecret(result.secret);
            setQrCode(result.qrCode);
            setOpenDialog(true);
        }
    };

    const handleConfirmTwoFA = async (otpCode: string) => {
        if (!userId || !qrSecret) {
            return false;
        }

        const success = await confirm2FA(Number(userId), qrSecret, otpCode);
        if (success) {
            setHas2FA(true);
            setQrSecret('');
            setQrCode('');
            // Update logged-in user with 2FA enabled
            if (setLoggedInUser && dbUser) {
                setLoggedInUser({ ...dbUser, totp_secret: qrSecret });
            }
        }
        return success;
    };

    const handleDisableTwoFA = async () => {
        if (!userId || !window.confirm('Are you sure you want to disable 2FA? You will need to re-enable it to use authenticator apps for login.')) {
            return;
        }

        setDisable2FALoading(true);
        const success = await disable2FA(Number(userId));
        setDisable2FALoading(false);

        if (success) {
            setHas2FA(false);
            // Update logged-in user with 2FA disabled
            if (setLoggedInUser && dbUser) {
                setLoggedInUser({ ...dbUser, totp_secret: null });
            }
        }
    };

    if (!msalUser && !dbUser) {
        return <div>No user information available.</div>;
    }

    return (
        <Box sx={{ p: 3, display: 'grid', gap: 3, maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='h5' sx={{ fontWeight: 700 }}>
                    User Profile
                </Typography>
                <Tooltip title='Documents'>
                    <IconButton
                        size='small'
                        color='primary'
                        onClick={(e) => {
                                e.stopPropagation();
                                handleViewDocs(userId, displayName);
                            }}
                        sx={{ ml: 1 }}
                    >
                        <FolderOpenIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Box>

            <Paper elevation={1} sx={{ p: 3 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '180px minmax(0, 1fr)' },
                        gap: 1.5,
                        alignItems: 'center',
                    }}
                >
                    <Typography fontWeight={700}>Name</Typography>
                    <Typography>{displayName}</Typography>
                    <Typography fontWeight={700}>Email</Typography>
                    <Typography>{displayEmail}</Typography>
                    <Typography fontWeight={700}>Company</Typography>
                    <Typography>{displayCompany}</Typography>
                    <Typography fontWeight={700}>Role</Typography>
                    <Typography>{displayRole}</Typography>
                    <Typography fontWeight={700}>Phone</Typography>
                    <Typography>{dbUser?.phone_number || '-'}</Typography>
                    <Typography fontWeight={700}>Country</Typography>
                    <Typography>{dbUser?.country || '-'}</Typography>
                    <Typography fontWeight={700}>NDA</Typography>
                    <Typography>
                        {isNdaTrue ? (
                            <CheckBox style={{ color: 'green' }} />
                        ) : (
                            <Cancel style={{ color: 'red' }} />
                        )}
                    </Typography>
                    <Typography fontWeight={700}>Environment</Typography>
                    <Typography>{msalUser?.environment || '-'}</Typography>
                    <Typography fontWeight={700}>Auth Status</Typography>
                    <Typography>{inProgress}</Typography>
                </Box>
            </Paper>

            <Box>
                <Button
                    variant='outlined'
                    onClick={handleToggleChangePassword}
                    sx={{ fontWeight: 600 }}
                >
                    {showChangePassword ? 'Cancel' : 'Change Password'}
                </Button>
            </Box>

            {showChangePassword && (
                <Paper elevation={1} sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <Typography variant='body2' color='text.secondary'>
               
                        </Typography>

                        {passwordError ? <Alert severity='error'>{passwordError}</Alert> : null}
                        {passwordSuccess ? <Alert severity='success'>{passwordSuccess}</Alert> : null}

                        <TextField
                            label='New Password'
                            type='password'
                            value={newPassword}
                            onChange={(event) => setNewPassword(event.target.value)}
                            autoComplete='new-password'
                            disabled={passwordLoading}
                            fullWidth
                        />
                        <TextField
                            label='Confirm New Password'
                            type='password'
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.target.value)}
                            autoComplete='new-password'
                            disabled={passwordLoading}
                            fullWidth
                        />

                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button variant='contained' onClick={handleChangePassword} disabled={passwordLoading}>
                                {passwordLoading ? 'Updating...' : 'Update Password'}
                            </Button>
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    setNewPassword('');
                                    setConfirmPassword('');
                                    setPasswordError('');
                                    setPasswordSuccess('');
                                }}
                                disabled={passwordLoading}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Stack>
                </Paper>
            )}

            {/* Two-Factor Authentication Section */}
            <Box>
                <Typography variant='h6' sx={{ fontWeight: 700, mb: 1 }}>
                    Two-Factor Authentication
                </Typography>
                <Paper elevation={1} sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Status: {has2FA ? '✓ Enabled' : 'Disabled'}
                                </Typography>
                                <Typography variant='caption' color='text.secondary'>
                                    Enhance your account security with TOTP-based 2FA using an authenticator app
                                </Typography>
                            </Box>
                        </Box>

                        {twoFAError && <Alert severity='error'>{twoFAError}</Alert>}

                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            {!has2FA ? (
                                <Button
                                    variant='contained'
                                    onClick={handleEnableTwoFA}
                                    disabled={twoFALoading}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {twoFALoading ? 'Generating...' : 'Enable 2FA'}
                                </Button>
                            ) : (
                                <Button
                                    variant='outlined'
                                    color='error'
                                    onClick={handleDisableTwoFA}
                                    disabled={disable2FALoading}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {disable2FALoading ? 'Disabling...' : 'Disable 2FA'}
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </Paper>
            </Box>

            <Box>
                <Button
                    type='button'
                    onClick={handleLogout}
                    variant='outlined'
                    color='error'
                    sx={{ fontWeight: 700 }}
                >
                    Logout
                </Button>
            </Box>

            <UserDocsForm
                open={docsDialogOpen}
                onClose={handleCloseDocsDialog}
                userId={selectedUserId}
                canUpload={(() => {
                    const authTokenStr = localStorage.getItem('AUTH_TOKEN');
                    const authUser = authTokenStr ? JSON.parse(authTokenStr) : null;
                    return Number(authUser?.role_id ?? authUser?.roleId) === 1;
                })()}
                onFileDrop={async (file, userId) => {
                    const pickedFile = {
                        file,
                        name: file.name,
                        path: file.name,
                        bytes: await file.arrayBuffer(),
                    };
                    const success = await uploadUserDocumentToAzure(pickedFile, userId);
                    if (!success) {
                        throw new Error('Failed to upload user document');
                    }
                }}
            />

            {/* 2FA Setup Dialog */}
            <TwoFASetupDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                secret={qrSecret}
                qrCode={qrCode}
                onConfirm={handleConfirmTwoFA}
                loading={twoFALoading}
            />
        </Box>
    );
};

export default ProfileTab;
